import React, { useState, useEffect } from "react";
import { Card, Table, Button, Alert, Badge } from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

interface WorklogEntry {
  id: number;
  user_id: number;
  file_name: string;
  year: number;
  month: number;
  uploaded_at: string;
  can_download: boolean;
}

const MONTH_NAMES = [
  "",
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

const EmployeeWorklog: React.FC = () => {
  const { t, dir } = useLanguage();
  const [worklogs, setWorklogs] = useState<WorklogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    api
      .get("/worklog/my-worklogs")
      .then((r) => setWorklogs(r.data.worklogs || []))
      .catch((err: any) => {
        if (err?.response?.status !== 404) setError(t("failedToLoad"));
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (w: WorklogEntry) => {
    setDownloadingId(w.id);
    try {
      const response = await api.get(`/worklog/download/${w.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", w.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t("downloadFailed"));
    } finally {
      setDownloadingId(null);
    }
  };

  // Group by year
  const byYear: Record<number, WorklogEntry[]> = {};
  for (const w of worklogs) {
    if (!byYear[w.year]) byYear[w.year] = [];
    byYear[w.year].push(w);
  }
  const years = Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a);

  if (loading) return <div className="text-center py-4">{t("loading")}</div>;

  return (
    <div dir={dir}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {worklogs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40 }}>📋</div>
          <p className="mb-1 fw-semibold">{t("noWorklogAvailable")}</p>
          <p className="small">{t("noWorklogMessage")}</p>
        </div>
      ) : (
        years.map((year) => (
          <Card key={year} className="mb-3 shadow-sm">
            <Card.Header className="fw-bold">{year}</Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("month")}</th>
                    <th>{t("fileName")}</th>
                    <th>{t("uploadDate")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {byYear[year]
                    .sort((a, b) => b.month - a.month)
                    .map((w) => (
                      <tr key={w.id}>
                        <td>
                          <Badge bg="secondary">{w.month}</Badge>{' '}
                          <small className="text-muted">
                            {MONTH_NAMES[w.month]}
                          </small>
                        </td>
                        <td>
                          <small>{w.file_name}</small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(w.uploaded_at).toLocaleDateString()}
                          </small>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={downloadingId === w.id}
                            onClick={() => handleDownload(w)}
                          >
                            {downloadingId === w.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              t("download")
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );
};

export default EmployeeWorklog;
